/**
 * GET /api/user 用户载荷解包与 localStorage 合并
 */
import {
  collectPermissionNamesFromFormGrants,
  getRegistrationFormGrantsFromUser,
  normalizeRegistrationFormGrant
} from '@/utils/registrationFormGrants'

/**
 * GET /api/user 响应解包（与 MainLayout 一致）
 * @param {unknown} res axios 拦截器返回的业务体
 */
export function unwrapAuthUserPayload (res) {
  if (!res || typeof res !== 'object') return {}
  const inner = res.data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    if (
      inner.name != null ||
      inner.email != null ||
      inner.id != null ||
      inner.roles != null ||
      inner.company != null ||
      inner.companies != null ||
      inner.resourceGrantPermissionNames != null ||
      inner.resource_grant_permission_names != null ||
      inner.registrationFormGrants != null ||
      inner.registration_form_grants != null
    ) {
      return inner
    }
  }
  if (
    res.name != null ||
    res.email != null ||
    res.id != null ||
    res.roles != null ||
    res.company != null ||
    res.companies != null ||
    res.resourceGrantPermissionNames != null ||
    res.resource_grant_permission_names != null ||
    res.registrationFormGrants != null ||
    res.registration_form_grants != null
  ) {
    return res
  }
  return {}
}

/**
 * @param {unknown} role
 * @returns {string}
 */
export function normalizeRoleName (role) {
  if (typeof role === 'string') return role.trim()
  if (role && typeof role === 'object') {
    const n = role.name ?? role.role_name ?? role.label
    return n != null ? String(n).trim() : ''
  }
  return ''
}

/**
 * @param {Record<string, unknown>} d unwrap 后的用户对象
 * @returns {string[]}
 */
export function extractRoleNamesFromUser (d) {
  const roles = d?.roles
  if (!Array.isArray(roles)) return []
  return roles.map(normalizeRoleName).filter(Boolean)
}

/** 归一化 registrationFormGrants 数组写入 storage */
function normalizeRegistrationFormGrantsForStorage (raw, fallback = []) {
  const list = Array.isArray(raw) ? raw : fallback
  return list.map(normalizeRegistrationFormGrant).filter(Boolean)
}

/**
 * 将登录/激活/GET user 中的 ACL 权限合并进 user 对象
 * @param {Record<string, unknown>|null|undefined} user
 * @param {Record<string, unknown>|null|undefined} authPayload
 */
export function mergeAuthGrantPermissionsIntoUser (user, authPayload) {
  const base = user && typeof user === 'object' ? { ...user } : {}

  const grantLists = [
    getRegistrationFormGrantsFromUser(base),
    getRegistrationFormGrantsFromUser(authPayload),
    getRegistrationFormGrantsFromUser(authPayload?.user)
  ]
  const formGrants = grantLists.find((g) => g.length) || []

  if (formGrants.length) {
    base.registrationFormGrants = formGrants
  }

  const sources = [
    base.resourceGrantPermissionNames,
    base.resource_grant_permission_names,
    authPayload?.resourceGrantPermissionNames,
    authPayload?.resource_grant_permission_names,
    authPayload?.user?.resourceGrantPermissionNames,
    authPayload?.user?.resource_grant_permission_names,
    collectPermissionNamesFromFormGrants(formGrants),
    collectPermissionNamesFromFormGrants(getRegistrationFormGrantsFromUser(authPayload)),
    collectPermissionNamesFromFormGrants(getRegistrationFormGrantsFromUser(authPayload?.user))
  ]
  const merged = []
  for (const raw of sources) {
    if (!Array.isArray(raw)) continue
    for (const name of raw) {
      const n = String(name).trim()
      if (n) merged.push(n)
    }
  }
  if (merged.length) {
    base.resourceGrantPermissionNames = [...new Set(merged)]
  }
  return base
}

/**
 * 将 GET /user 返回的用户字段合并进 localStorage.user（保留 token 登录时的其它字段）
 * @param {Record<string, unknown>} cached 现有 localStorage.user
 * @param {Record<string, unknown>} fresh unwrap 后的最新用户
 */
export function mergeUserIntoStoragePayload (cached, fresh) {
  const base = cached && typeof cached === 'object' ? { ...cached } : {}
  const next = fresh && typeof fresh === 'object' ? fresh : {}
  const registrationFormGrants = normalizeRegistrationFormGrantsForStorage(
    next.registrationFormGrants ?? next.registration_form_grants,
    getRegistrationFormGrantsFromUser(base)
  )
  return mergeAuthGrantPermissionsIntoUser({
    ...base,
    ...next,
    roles: next.roles ?? base.roles,
    company: next.company ?? base.company,
    companies: next.companies ?? base.companies,
    company_id: next.company_id ?? next.companyId ?? base.company_id ?? base.companyId,
    registrationFormGrants: registrationFormGrants.length
      ? registrationFormGrants
      : (base.registrationFormGrants ?? undefined)
  }, next)
}
